import { PERSONAL_PLAN_STEP_ID } from '@/lib/personalPlan';
import { supabase } from "@/integrations/supabase/client";
import { OnboardingConfig, OnboardingStep, PROFILE_FIELDS } from "@/config/onboardingConfig";
import { Json } from "@/integrations/supabase/types";

// קבלת קונפיגורציית אונבורדינג מהדאטהבייס
export const getOnboardingConfig = async (): Promise<OnboardingConfig | null> => {
  const { data, error } = await supabase
    .from('onboarding_config')
    .select('*')
    .eq('is_active', true)
    .limit(1)
    .single();

  if (error || !data) {
    console.log('No active onboarding config found');
    return null;
  }

  return {
    steps: data.steps as unknown as OnboardingStep[],
    brandName: data.brand_name || 'האפליקציה שלי',
    totalSteps: data.total_steps || 0
  };
};

// שמירת קונפיגורציית אונבורדינג לדאטהבייס
export const saveOnboardingConfig = async (config: OnboardingConfig): Promise<boolean> => {
  // בדיקה אם יש קונפיגורציה קיימת
  const { data: existing } = await supabase
    .from('onboarding_config')
    .select('id')
    .eq('is_active', true)
    .limit(1)
    .single();

  if (existing) {
    // עדכון קונפיגורציה קיימת
    const { error } = await supabase
      .from('onboarding_config')
      .update({
        steps: config.steps as unknown as Json,
        brand_name: config.brandName,
        total_steps: config.totalSteps,
        updated_at: new Date().toISOString()
      })
      .eq('id', existing.id);

    if (error) {
      console.error('Error updating onboarding config:', error);
      return false;
    }
  } else {
    // יצירת קונפיגורציה חדשה
    const { error } = await supabase
      .from('onboarding_config')
      .insert({
        steps: config.steps as unknown as Json,
        brand_name: config.brandName,
        total_steps: config.totalSteps,
        is_active: true
      });

    if (error) {
      console.error('Error creating onboarding config:', error);
      return false;
    }
  }

  return true;
};

// שמירת תשובת משתמש לשלב באונבורדינג
export const saveUserAnswer = async (
  userId: string,
  stepId: string,
  answer: unknown
): Promise<boolean> => {
  console.log('[OnboardingService] ========== saveUserAnswer ==========');
  console.log('[OnboardingService] User ID:', userId);
  console.log('[OnboardingService] Step ID:', stepId);
  console.log('[OnboardingService] Answer:', answer);

  const { data, error } = await supabase
    .from('user_onboarding_answers')
    .upsert({
      user_id: userId,
      step_id: stepId,
      answer: answer as Json
    }, { onConflict: 'user_id,step_id' })
    .select();

  if (error) {
    console.error('[OnboardingService] Error saving user answer:', error);
    return false;
  }

  console.log('[OnboardingService] Answer saved successfully:', data);
  return true;
};

// קבלת כל התשובות של משתמש
export const getUserAnswers = async (userId: string): Promise<Record<string, unknown>> => {
  console.log('[OnboardingService] ========== getUserAnswers ==========');
  console.log('[OnboardingService] User ID:', userId);

  const { data, error } = await supabase
    .from('user_onboarding_answers')
    .select('step_id, answer')
    .eq('user_id', userId);

  if (error || !data) {
    console.error('[OnboardingService] Error getting user answers:', error);
    return {};
  }

  const answers = data.reduce((acc, item) => {
    acc[item.step_id] = item.answer;
    return acc;
  }, {} as Record<string, unknown>);

  console.log('[OnboardingService] Retrieved answers:', answers);
  console.log('[OnboardingService] Total answers count:', data.length);
  return answers;
};

// עדכון פרופיל מתשובות אונבורדינג - תומך במיפוי דינמי
export const syncAnswersToProfile = async (
  userId: string,
  answers: Record<string, unknown>,
  steps?: OnboardingStep[]
): Promise<boolean> => {
  console.log('[OnboardingService] ========== syncAnswersToProfile ==========');
  console.log('[OnboardingService] User ID:', userId);
  console.log('[OnboardingService] Answers to sync:', answers);
  console.log('[OnboardingService] Steps config provided:', !!steps);

  const profileUpdates: Record<string, unknown> = {};
  const customFieldsUpdates: Record<string, unknown> = {};

  // פונקציה לחילוץ ערך מתשובה - תומכת גם באובייקטים מקוננים
  const extractValue = (answer: unknown): unknown => {
    console.log('[OnboardingService] extractValue input:', JSON.stringify(answer));
    if (answer === null || answer === undefined) return null;
    if (typeof answer === 'object' && !Array.isArray(answer)) {
      const obj = answer as Record<string, unknown>;
      
      // טיפול בתאריך מ-DatePicker - פורמט: { date: "..." } או { date: Date } או { date: { _type: "Date", value: { iso: "..." } } }
      if (obj.date !== undefined) {
        console.log('[OnboardingService] Found date field:', JSON.stringify(obj.date));
        console.log('[OnboardingService] Date type:', typeof obj.date, 'is Date instance:', obj.date instanceof Date);
        
        // אם התאריך הוא Date object ממש (מגיע מ-Supabase כ-Date)
        if (obj.date instanceof Date) {
          const isoString = (obj.date as Date).toISOString();
          console.log('[OnboardingService] Date is Date object, converting to ISO:', isoString);
          return isoString;
        }
        
        // אם התאריך כבר string (ISO format)
        if (typeof obj.date === 'string') {
          console.log('[OnboardingService] Date is already string, returning:', obj.date);
          return obj.date;
        }
        
        // אם התאריך הוא מספר (timestamp)
        if (typeof obj.date === 'number') {
          const dateFromNumber = new Date(obj.date);
          if (!isNaN(dateFromNumber.getTime())) {
            const isoString = dateFromNumber.toISOString();
            console.log('[OnboardingService] Date is number timestamp, converting to ISO:', isoString);
            return isoString;
          }
        }
        
        // אם התאריך הוא אובייקט מקונן: { _type: "Date", value: { iso: "..." } }
        if (typeof obj.date === 'object' && obj.date !== null) {
          const dateObj = obj.date as Record<string, unknown>;
          console.log('[OnboardingService] Date is object:', JSON.stringify(dateObj));
          
          // פורמט: { _type: "Date", value: { iso: "...", value: ..., local: "..." } }
          if (dateObj._type === 'Date' && dateObj.value && typeof dateObj.value === 'object') {
            const valueObj = dateObj.value as Record<string, unknown>;
            const isoDate = valueObj.iso as string;
            console.log('[OnboardingService] Extracted ISO date from nested structure:', isoDate);
            return isoDate;
          }
          
          // פורמט ישיר עם iso
          if (dateObj.iso) {
            console.log('[OnboardingService] Extracted direct ISO:', dateObj.iso);
            return dateObj.iso;
          }
        }
      }
      
      const values = Object.values(obj);
      if (values.length === 1) return extractValue(values[0]); // רקורסיבי לטיפול במבנים מקוננים
      if (obj.text) return obj.text;
      if (obj.value) return obj.value;
      if (obj.display_name) return obj.display_name;
      if (obj.selections) return obj.selections;
      if (obj.selection) return obj.selection;
      return values[0];
    }
    return answer;
  };

  // פונקציה להמרת ערך לפי סוג השדה
  const convertValue = (value: unknown, fieldType?: string, fieldName?: string): unknown => {
    if (value === null || value === undefined || value === '') return null;
    
    switch (fieldType) {
      case 'number':
        // אם זה שדה גיל ויש לנו תאריך - חשב גיל
        if (fieldName === 'age' && typeof value === 'string' && value.includes('-')) {
          const birthDate = new Date(value);
          if (!isNaN(birthDate.getTime())) {
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            console.log('[OnboardingService] Calculated age from birth date:', value, '->', age);
            return age;
          }
        }
        return parseInt(value as string) || null;
      case 'array':
        if (Array.isArray(value)) return (value as string[]).join(', ');
        return value;
      default:
        return value;
    }
  };

  // רשימת שדות פרופיל תקינים לעדכון
  const validProfileFields = PROFILE_FIELDS.map(f => f.value) as string[];

  // מיפוי fallback לשדות סטנדרטיים (תאימות לאחור)
  const fallbackMappings: Record<string, { fieldName: string; fieldType: string }> = {
    'name': { fieldName: 'display_name', fieldType: 'text' },
    'display_name': { fieldName: 'display_name', fieldType: 'text' },
    'age': { fieldName: 'age', fieldType: 'number' },
    'location': { fieldName: 'location', fieldType: 'text' },
    'about': { fieldName: 'about_me', fieldType: 'text' },
    'about_me': { fieldName: 'about_me', fieldType: 'text' },
    'hobbies': { fieldName: 'hobbies', fieldType: 'text' },
    'pronouns': { fieldName: 'pronouns', fieldType: 'text' },
  };

  // עיבוד כל התשובות
  for (const [stepId, answer] of Object.entries(answers)) {
    console.log('[OnboardingService] Processing stepId:', stepId, 'answer:', JSON.stringify(answer));
    if (!answer) continue;

    const value = extractValue(answer);
    console.log('[OnboardingService] Extracted value:', value);
    if (value === null || value === undefined || value === '') continue;

    // חיפוש הגדרת מיפוי בשלב (אם steps סופקו)
    const stepConfig = steps?.find(s => s.id === stepId);
    console.log('[OnboardingService] stepConfig for', stepId, ':', stepConfig ? JSON.stringify({
      id: stepConfig.id,
      type: stepConfig.type,
      profileFieldMapping: stepConfig.profileFieldMapping,
      profile_field_mapping: (stepConfig as unknown as Record<string, unknown>).profile_field_mapping,
      datePickerConfig: stepConfig.datePickerConfig
    }) : 'NOT FOUND');
    
    // תמיכה גם ב-camelCase וגם ב-snake_case
    const mapping = stepConfig?.profileFieldMapping || 
                    (stepConfig as unknown as Record<string, unknown>)?.profile_field_mapping as { fieldName: string; isNewField?: boolean; fieldType?: string } | undefined;
    
    console.log('[OnboardingService] Final mapping:', mapping);
    
    if (mapping) {
      const { fieldName, isNewField, fieldType } = mapping;
      const fieldLabel = (mapping as { fieldLabel?: string }).fieldLabel;
      console.log('[OnboardingService] Using mapping - fieldName:', fieldName, 'fieldLabel:', fieldLabel, 'fieldType:', fieldType, 'value:', value);
      
      // אם זה שדה גיל וקיבלנו תאריך - חשב גיל
      let valueToConvert = value;
      if (fieldName === 'age' && fieldType === 'number' && stepConfig?.type === 'date-picker') {
        console.log('[OnboardingService] Age field from date-picker, calculating age from date');
        valueToConvert = value; // נשמור את התאריך, convertValue יחשב את הגיל
      }
      
      // אם זה תחביבים או כל מערך - המר values ל-labels ואז ל-string מופרד בפסיקים
      if (Array.isArray(value) && stepConfig?.options) {
        const labels = (value as string[]).map(val => {
          const option = stepConfig.options?.find((opt: { value: string; label: string }) => opt.value === val);
          return option?.label || val;
        });
        // אם השדה הוא text - המר ל-string מופרד בפסיקים
        valueToConvert = fieldType === 'text' ? labels.join(', ') : labels;
        console.log('[OnboardingService] Converted array values to labels:', labels, '-> final:', valueToConvert);
      }
      
      const convertedValue = convertValue(valueToConvert, fieldType, fieldName);
      console.log('[OnboardingService] Converted value:', convertedValue);
      
      if (isNewField) {
        // שמירת שדה חדש עם תווית
        customFieldsUpdates[fieldName] = {
          value: convertedValue,
          label: fieldLabel || fieldName
        };
      } else {
        // שדה קיים - שמור את הערך ואת התווית הדינמית אם קיימת
        if (validProfileFields.includes(fieldName)) {
          profileUpdates[fieldName] = convertedValue;
        }
        // שמור תווית דינמית לשדה קיים אם סופקה
        if (fieldLabel && fieldLabel !== fieldName) {
          if (!profileUpdates._fieldLabels) {
            profileUpdates._fieldLabels = {};
          }
          (profileUpdates._fieldLabels as Record<string, string>)[fieldName] = fieldLabel;
        }
      }
    }
    // FALLBACK אוטומטי: אם זה date-picker ויש תאריך - זה כנראה גיל
    // בודק גם לפי stepConfig.type וגם לפי stepId שמכיל 'date'
    else if (
      (stepConfig?.type === 'date-picker' || stepId.toLowerCase().includes('date') || stepId === 'date-picker') && 
      typeof value === 'string' && 
      value.includes('-')
    ) {
      console.log('[OnboardingService] Auto-mapping date-picker to age field, stepId:', stepId, 'value:', value);
      const age = convertValue(value, 'number', 'age');
      console.log('[OnboardingService] Calculated age:', age);
      if (age !== null && typeof age === 'number' && age > 0 && age < 150) {
        profileUpdates['age'] = age;
        console.log('[OnboardingService] Age saved to profileUpdates:', age);
      }
    }
    else if (fallbackMappings[stepId]) {
      // שימוש במיפוי fallback לתאימות לאחור
      const { fieldName, fieldType } = fallbackMappings[stepId];
      console.log('[OnboardingService] Using fallback mapping for stepId:', stepId, '-> fieldName:', fieldName);
      profileUpdates[fieldName] = convertValue(value, fieldType, fieldName);
    } else if (typeof answer === 'object' && !Array.isArray(answer)) {
      // חיפוש מפתחות מוכרים בתוך האובייקט
      const answerObj = answer as Record<string, unknown>;
      for (const [key, val] of Object.entries(answerObj)) {
        if (fallbackMappings[key] && val !== null && val !== undefined && val !== '') {
          const { fieldName, fieldType } = fallbackMappings[key];
          console.log('[OnboardingService] Using fallback mapping for key:', key, '-> fieldName:', fieldName);
          profileUpdates[fieldName] = convertValue(val, fieldType, fieldName);
        }
      }
    }
  }

  // שמירת שדות מותאמים אישית ותוויות דינמיות ב-unlocked_features
  const hasCustomFields = Object.keys(customFieldsUpdates).length > 0;
  const hasFieldLabels = profileUpdates._fieldLabels && Object.keys(profileUpdates._fieldLabels as Record<string, string>).length > 0;
  
  if (hasCustomFields || hasFieldLabels) {
    // קודם נקרא את הערכים הקיימים
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('unlocked_features')
      .eq('id', userId)
      .single();

    const existingFeatures = (existingProfile?.unlocked_features as Record<string, unknown>) || {};
    const unlockedFeaturesUpdate: Record<string, unknown> = { ...existingFeatures };
    
    // שמירת שדות מותאמים אישית
    if (hasCustomFields) {
      unlockedFeaturesUpdate.custom_onboarding_fields = {
        ...((existingFeatures.custom_onboarding_fields as Record<string, unknown>) || {}),
        ...customFieldsUpdates
      };
    }
    
    // שמירת תוויות דינמיות לשדות קיימים
    if (hasFieldLabels) {
      unlockedFeaturesUpdate.field_labels = {
        ...((existingFeatures.field_labels as Record<string, string>) || {}),
        ...(profileUpdates._fieldLabels as Record<string, string>)
      };
    }
    
    profileUpdates.unlocked_features = unlockedFeaturesUpdate;
  }
  
  // הסרת שדה עזר פנימי
  delete profileUpdates._fieldLabels;

  console.log('[OnboardingService] Profile updates to apply:', profileUpdates);
  console.log('[OnboardingService] Custom fields updates:', customFieldsUpdates);

  if (Object.keys(profileUpdates).length === 0) {
    console.log('[OnboardingService] No profile updates to apply');
    return true;
  }

  const { data, error } = await supabase
    .from('profiles')
    .update(profileUpdates as any)
    .eq('id', userId)
    .select();

  if (error) {
    console.error('[OnboardingService] Error syncing answers to profile:', error);
    return false;
  }

  console.log('[OnboardingService] Profile updated successfully:', data);
  return true;
};

// סימון סיום אונבורדינג
export const completeOnboarding = async (userId: string): Promise<boolean> => {
  console.log('[OnboardingService] ========== completeOnboarding ==========');
  console.log('[OnboardingService] User ID:', userId);

  const { data, error } = await supabase
    .from('profiles')
    .update({ 
      has_completed_onboarding: true,
      current_onboarding_step_id: null 
    })
    .eq('id', userId)
    .select();

  if (error) {
    console.error('[OnboardingService] Error completing onboarding:', error);
    return false;
  }

  console.log('[OnboardingService] Onboarding completed successfully:', data);
  return true;
};

// עדכון שלב נוכחי באונבורדינג לפי step ID
export const updateCurrentStepId = async (userId: string, stepId: string): Promise<boolean> => {
  console.log('[OnboardingService] ========== updateCurrentStepId ==========');
  console.log('[OnboardingService] User ID:', userId);
  console.log('[OnboardingService] New Step ID:', stepId);

  const { data, error } = await supabase
    .from('profiles')
    .update({ current_onboarding_step_id: stepId })
    .eq('id', userId)
    .select();

  if (error) {
    console.error('[OnboardingService] Error updating current step:', error);
    return false;
  }

  console.log('[OnboardingService] Current step updated successfully:', data);
  return true;
};

// מחיקת תשובות של שלבים שנמחקו
export const cleanupDeletedStepAnswers = async (
  userId: string,
  activeStepIds: string[]
): Promise<{ deleted: number }> => {
  console.log('[OnboardingService] ========== cleanupDeletedStepAnswers ==========');
  console.log('[OnboardingService] User ID:', userId);
  console.log('[OnboardingService] Active step IDs:', activeStepIds);

  // מצא תשובות שה-step_id שלהן לא קיים ברשימת השלבים הפעילים
  const { data: existingAnswers, error: fetchError } = await supabase
    .from('user_onboarding_answers')
    .select('step_id')
    .eq('user_id', userId);

  if (fetchError || !existingAnswers) {
    console.error('[OnboardingService] Error fetching existing answers:', fetchError);
    return { deleted: 0 };
  }

  // מצא step_ids שצריך למחוק (לא קיימים ברשימת השלבים הפעילים)
  const stepIdsToDelete = existingAnswers
    .map(a => a.step_id)
    .filter(stepId => !activeStepIds.includes(stepId))
    // Not an onboarding step and never one: the personal plan built at funnel
    // signup lives in this table under its own id.
    .filter(stepId => stepId !== PERSONAL_PLAN_STEP_ID);

  console.log('[OnboardingService] Step IDs to delete:', stepIdsToDelete);

  if (stepIdsToDelete.length === 0) {
    return { deleted: 0 };
  }

  const { error: deleteError } = await supabase
    .from('user_onboarding_answers')
    .delete()
    .eq('user_id', userId)
    .in('step_id', stepIdsToDelete);

  if (deleteError) {
    console.error('[OnboardingService] Error deleting orphaned answers:', deleteError);
    return { deleted: 0 };
  }

  console.log('[OnboardingService] Deleted', stepIdsToDelete.length, 'orphaned answers');
  return { deleted: stepIdsToDelete.length };
};
